import { v4 as uuidv4 } from "uuid";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import { callFunction } from "../../api/callFunction";
import { storage } from "../../firebase";
import { capacityErrors } from "./eventCapacity";

export const convertDataUrlToBlob = (dataUrl) => {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1];
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new Blob([u8arr], { type: mime });
};

const uploadSinglePhoto = async (photo, eventId) => {
  if (typeof photo === "string") {
    if (photo.startsWith("data:")) {
      const blob = convertDataUrlToBlob(photo);
      const uniqueName = `${uuidv4()}_generated.png`;
      const photoRef = ref(storage, `event/${eventId}/eventPhotos/${uniqueName}`);
      await uploadBytes(photoRef, blob, {
        cacheControl: "public, max-age=31536000, immutable",
        contentType: blob.type,
      });
      return getDownloadURL(photoRef);
    }
    // Already in Firebase Storage — permanent, keep as-is.
    if (photo.startsWith("https://firebasestorage.googleapis.com/")) {
      return photo;
    }
    // External URL (e.g. Google Places photoUri) — these expire, so download and re-upload
    // via a Cloud Function to avoid CORS restrictions on browser-side fetches.
    const data = await callFunction("fetchExternalImage", { url: photo });
    const dataUrl = data.dataUrl;
    const blob = convertDataUrlToBlob(dataUrl);
    const ext = (blob.type.split("/")[1] || "jpg").split(";")[0];
    const uniqueName = `${uuidv4()}_external.${ext}`;
    const photoRef = ref(storage, `event/${eventId}/eventPhotos/${uniqueName}`);
    await uploadBytes(photoRef, blob, {
      cacheControl: "public, max-age=31536000, immutable",
      contentType: blob.type,
    });
    return getDownloadURL(photoRef);
  }
  const uniqueName = `${uuidv4()}_${photo.name || "photo.jpg"}`;
  const photoRef = ref(storage, `event/${eventId}/eventPhotos/${uniqueName}`);
  await uploadBytes(photoRef, photo, {
    cacheControl: "public, max-age=31536000, immutable",
    contentType: photo.type,
  });
  return getDownloadURL(photoRef);
};

export const uploadEventPhotos = async (photos, eventId) => {
  if (!photos || photos.length === 0) return [];
  return Promise.all(photos.map((photo) => uploadSinglePhoto(photo, eventId)));
};

/**
 * A photo that is not yet permanently ours: the resized File the upload tile
 * produces, the `data:image/png;base64,...` string Generate returns, or an
 * http(s) URL pointing at somebody else's server.
 *
 * That last case is the one worth spelling out. uploadSinglePhoto downloads and
 * re-hosts external URLs because they EXPIRE — a Google Places photoUri is the
 * example in its own comment. Treating a pasted URL as already-permanent would
 * leave the variant working on the day it was saved and broken weeks later, on
 * a live Meetup listing and on the event page, with nothing in the admin panel
 * hinting at why. A save that fails on an unfetchable link is the better
 * failure: it is visible, immediate, and the author can fix it.
 *
 * Only a Firebase Storage URL is genuinely settled, which is exactly the one
 * case uploadSinglePhoto passes through untouched, so re-saving an unchanged
 * event still uploads nothing.
 */
export const isPendingPhoto = (photo) => {
  if (!photo) return false;
  if (typeof photo !== "string") return true;
  return !photo.startsWith("https://firebasestorage.googleapis.com/");
};

export const hasPendingVariantPhoto = (listingVariants) =>
  Object.values(listingVariants || {}).some((variant) =>
    isPendingPhoto(variant?.photo)
  );

/**
 * Upload any pending variant photo and return the map with storage URLs in place
 * of the pending values.
 *
 * A variant photo reaches form state exactly as an event photo does, as a File
 * or a base64 data URL, and `listingVariants` is written straight into the event
 * doc. Neither of those may land there: a base64 PNG is usually over Firestore's
 * 1MB document limit, so the save fails outright, and if a smaller one did land
 * the backend would hand that string to Meetup and Eventbrite as an image URL.
 * So the same upload the event's own photos get happens here, before the write.
 *
 * Every route into this field is covered by uploadSinglePhoto's own branching:
 * the library returns a storage URL (passed through), Generate returns a data
 * URL, Upload returns a File, and a pasted third-party URL is re-hosted so it
 * cannot expire out from under a live listing. The variant photo therefore
 * behaves exactly like one of the event's own photos, which is the point.
 */
export const uploadListingVariantPhotos = async (listingVariants, eventId) => {
  const variants = listingVariants || {};
  const ids = Object.keys(variants);
  if (ids.length === 0) return variants;

  const entries = await Promise.all(
    ids.map(async (id) => {
      const variant = variants[id];
      if (!isPendingPhoto(variant?.photo)) return [id, variant];
      return [id, { ...variant, photo: await uploadSinglePhoto(variant.photo, eventId) }];
    })
  );

  return Object.fromEntries(entries);
};

/**
 * Merges two objects, recursively merging nested objects.
 * Preserves original array values when the update array is empty.
 */
export const mergeObjects = (original, updates) => {
  const merged = { ...original };

  for (const key in updates) {
    if (updates.hasOwnProperty(key)) {
      if (Array.isArray(updates[key])) {
        if (updates[key].length === 0 && Array.isArray(merged[key]) && merged[key].length > 0) {
          continue;
        }
        merged[key] = updates[key];
      } else if (typeof updates[key] === "object" && updates[key] !== null) {
        merged[key] = mergeObjects(merged[key] || {}, updates[key]);
      } else {
        merged[key] = updates[key];
      }
    }
  }

  return merged;
};

export const buildEventUsers = (event, usersDb) => {
  let eventUsers = {};
  usersDb.forEach((u) => {
    const isNewUser = !event.eventUsers || !event.eventUsers[u.userID];
    eventUsers[u.userID] = {
      userID: u.userID,
      name: u.name || null,
      photoURL: u.photoURL || null,
      ...(isNewUser && { allowTextNotifications: false }),
      consents: [],
      socialMedia: u.socialMedia || null,
    };
  });

  const eventUsersArrayExists =
    event?.eventUsers && Object.keys(event.eventUsers).length > 0;
  if (eventUsersArrayExists) {
    eventUsers = mergeObjects(event.eventUsers, eventUsers);
  }

  const allActiveUserIds = new Set([
    ...(event.rsvpMovers || []),
    ...(event.rsvpSitters || []),
    ...(event.movers || []),
    ...(event.sitters || []),
    ...(event.invitedSitters || []),
  ]);

  const filteredEventUsers = {};
  Object.keys(eventUsers).forEach((userId) => {
    if (allActiveUserIds.has(userId)) {
      filteredEventUsers[userId] = eventUsers[userId];
    }
  });

  return filteredEventUsers;
};

const MS_PER_DAY = 1000 * 60 * 60 * 24;
export const MIN_LEAD_DAYS = 21;

export const getDateWarning = (date) => {
  if (!(date instanceof Date) || isNaN(date.getTime())) return null;
  const now = new Date();
  const daysOut = (date.getTime() - now.getTime()) / MS_PER_DAY;
  if (daysOut < 0) return null;
  if (daysOut < MIN_LEAD_DAYS) {
    const days = Math.ceil(daysOut);
    return `Event is only ${days} day${days === 1 ? "" : "s"} away — recommended lead time is at least ${MIN_LEAD_DAYS} days.`;
  }
  return null;
};

// Postponing an event later than about a week hurts turnout, and the drop grows
// the further out the date moves. Returns a heads-up only when the edited date is
// more than a week later than the saved one; null otherwise (earlier moves, small
// shifts, missing/invalid dates).
export const DATE_PUSH_WARNING_DAYS = 7;

export const getDatePushWarning = (savedDate, currentDate) => {
  if (!(savedDate instanceof Date) || isNaN(savedDate.getTime())) return null;
  if (!(currentDate instanceof Date) || isNaN(currentDate.getTime())) return null;
  const daysLater = (currentDate.getTime() - savedDate.getTime()) / MS_PER_DAY;
  if (daysLater <= DATE_PUSH_WARNING_DAYS) return null;
  const weeks = Math.round(daysLater / 7);
  const span = weeks <= 1 ? "more than a week" : `about ${weeks} weeks`;
  return `Pushing the date ${span} later will likely reduce attendance — turnout tends to drop the further out you move an event.`;
};

// `checkCapacity` is for the save path (GIS-266). The image generators run
// this too, before a host has necessarily chosen a capacity.
export const validateEventFields = (
  event,
  requirePhotos = true,
  isEditMode = false,
  { checkCapacity = false } = {}
) => {
  const errors = [];
  const warnings = [];

  const nameWords =
    event.name
      ?.trim()
      .split(/\s+/)
      .filter((word) => word.length > 0) || [];
  if (nameWords.length <= 2) {
    errors.push("Event name must have more than 3 words");
  }

  if (!event.date) {
    errors.push("Event date is required");
  } else if (event.date instanceof Date && !isNaN(event.date.getTime())) {
    if (!isEditMode && event.date.getTime() < Date.now()) {
      errors.push("Event date must be in the future");
    } else if (!isEditMode) {
      const dateWarning = getDateWarning(event.date);
      if (dateWarning) warnings.push(dateWarning);
    }
  }

  const descWords =
    event.description
      ?.trim()
      .split(/\s+/)
      .filter((word) => word.length > 0) || [];
  if (descWords.length <= 10) {
    errors.push("Event description must have more than 10 words");
  }

  if (!event.hostname?.trim()) {
    errors.push("Venue name is required");
  }

  if (!event.address?.trim()) {
    errors.push("Address is required");
  }

  if (requirePhotos && (!event.photos || event.photos.length === 0)) {
    errors.push("At least one photo is required");
  }

  if (checkCapacity) {
    errors.push(...capacityErrors(event, isEditMode));
  }

  if (event.priceSchedule && event.priceSchedule.length > 0) {
    const dayGroups = {};
    const seatGroups = {};

    event.priceSchedule.forEach((tier, i) => {
      const hasTime = tier.daysBeforeEvent != null;
      const hasSeat = tier.seatThreshold != null;
      const type = hasTime && hasSeat ? "hybrid" : hasSeat ? "seats" : "schedule";
      const label = `Price tier ${i + 1}`;

      if (tier.price == null || isNaN(tier.price) || tier.price < 0) {
        errors.push(`${label}: price must be 0 or more`);
      }

      if (type === "schedule" || type === "hybrid") {
        const days = tier.daysBeforeEvent;
        if (days == null || isNaN(days) || days < 0) {
          errors.push(`${label}: days before event must be 0 or more`);
        } else {
          if (!dayGroups[days]) dayGroups[days] = [];
          dayGroups[days].push(i + 1);
        }
      }

      if (type === "seats" || type === "hybrid") {
        const seats = tier.seatThreshold;
        if (seats == null || isNaN(seats) || seats <= 0) {
          errors.push(`${label}: seat threshold must be at least 1`);
        } else {
          if (!seatGroups[seats]) seatGroups[seats] = [];
          seatGroups[seats].push(i + 1);
        }
      }
    });

    Object.entries(dayGroups).forEach(([days, indices]) => {
      if (indices.length > 1) {
        errors.push(
          `Price tiers ${indices.join(" and ")} have the same days before event (${days}) — remove the duplicate`
        );
      }
    });

    Object.entries(seatGroups).forEach(([seats, indices]) => {
      if (indices.length > 1) {
        errors.push(
          `Price tiers ${indices.join(" and ")} have the same seat threshold (${seats}) — remove the duplicate`
        );
      }
    });
  }

  return { errors, warnings };
};
